import { useEffect } from "react";
import { useFeatureFlagsStore } from "@src/stores/featureFlags";
import { useFlagWrapper } from "./useFlagWrapper";
import { useMeritAuth0 } from "@src/hooks";
import { useUnleashContext } from "@unleash/proxy-client-react";
import type { FeatureFlagNamesToValues } from "@src/stores/featureFlags";

/**
 * This component aims to achieve the following:
 * - synchronize flag values in the featureFlagStore
 *   - this is to enable a single point-of-entry hook `useFeatureFlags()` call within application code
 * - encapsulate Unleash hooks usage
 *
 * It is worth noting that an implementation detail of this component is usage of individual
 * `useFlag` hooks instead of an available `useFlags` hook from `@unleash/proxy-client-react`.
 * Unfortunately, the `useFlags` hook does _not_ include metrics reporting, only the individual
 * `useFlag` (and by extension, `getVariant`) hook does. The source code reflects this.
 *
 * See also a [Confluence doc](https://claimyourmerit.atlassian.net/wiki/spaces/PE/pages/3186622466/Review+Unleash+Integration+Docs+PF-1891#Observations-from-actual-use)
 * regarding observations from practical usage of the unleash library.
 */
export const FlagEffect = () => {
  const memberAppAllowAutoLanguageDetection = useFlagWrapper("memberAppAllowAutoLanguageDetection");
  const showMemberAppOpportunitiesFeature = useFlagWrapper("showMemberAppOpportunitiesFeature");
  const showMemberAppVerifications = useFlagWrapper("showMemberAppVerifications");
  const showOnboardingMerits = useFlagWrapper("showOnboardingMerits");
  const showMemberAppSettingsSectionPushNotification = useFlagWrapper(
    "showMemberAppSettingsSectionPushNotification"
  );
  const allowMemberAppPushNotificationRequestConsentOnColdStart = useFlagWrapper(
    "allowMemberAppPushNotificationRequestConsentOnColdStart"
  );
  const allowMemberAppVersionGating = useFlagWrapper("allowMemberAppVersionGating");

  const updateContext = useUnleashContext();

  const { user } = useMeritAuth0();

  const upsertFeatureFlags = useFeatureFlagsStore(state => state.upsertFeatureFlags);

  useEffect(() => {
    const flags: FeatureFlagNamesToValues = {
      allowMemberAppPushNotificationRequestConsentOnColdStart,
      allowMemberAppVersionGating,
      memberAppAllowAutoLanguageDetection,
      showMemberAppOpportunitiesFeature,
      showMemberAppSettingsSectionPushNotification,
      showMemberAppVerifications,
      showOnboardingMerits,
    };
    upsertFeatureFlags(flags);
  }, [
    allowMemberAppPushNotificationRequestConsentOnColdStart,
    showMemberAppSettingsSectionPushNotification,
    memberAppAllowAutoLanguageDetection,
    showMemberAppOpportunitiesFeature,
    showMemberAppVerifications,
    showOnboardingMerits,
    upsertFeatureFlags,
    allowMemberAppVersionGating,
  ]);

  useEffect(() => {
    updateContext({
      userId: user?.entityID,
    });
  }, [updateContext, user]);

  return null;
};
